import { BottomButtons, Button, Col } from '@landsbankinn/ui-kit';
import { useEffect, useState } from 'react';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  type Account,
  ChoiceType,
  type ScopeData,
  ScopeDataFormat,
} from '../../../state/server';
import { useConsentClientContext } from '../State/consentClientState';
import { getAvailableAccounts, toggleSelected } from '../utils';
import { AccountSelection } from './AccountSelection';
import { AccountsTable } from './AccountsTable';
import { FooterWithButtons } from './FooterWithButtons';
import { MobileTitleAndDescription } from './MobileTitleAndDescription';

export function SelectableListView(props: {
  data: ScopeData;
  previousTitle: string;
  nextTitle: string;
  isLastView: boolean;
  title?: string;
  description?: string;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const { isMobile } = useScreenSize();
  const [consentClient, consentDispatch] = useConsentClientContext();
  const format = props.data.format;

  const [accounts, setAccounts] = useState<Account[]>([]);

  useEffect(() => {
    if (consentClient.scope) {
      setAccounts(
        getAvailableAccounts(props.data, consentClient.scope?.accounts),
      );
    }
  }, [consentClient.scope, props.data]);

  const onAccountChange = (accountId: string) => {
    let seleted: string[] = consentClient.values[props.data.name] ?? [];
    if (props.data.choiceType === ChoiceType.Single) {
      seleted = toggleSelected(accountId, []);
    }
    if (props.data.choiceType === ChoiceType.Multiple) {
      seleted = toggleSelected(accountId, seleted);
    }

    const values: Record<string, string[]> = {};
    values[props.data.name] = seleted;
    consentDispatch({ type: 'setValues', payload: { values: values } });

    return Promise.resolve();
  };

  const onSelectAll = (ids: string[]) => {
    const values: Record<string, string[]> = {};
    values[props.data.name] = ids;
    consentDispatch({ type: 'setValues', payload: { values } });
  };

  const hideNext = isMobile && props.data.choiceType === ChoiceType.Single;
  const isDisabled =
    consentClient.values[props.data.name] !== undefined
      ? consentClient.values[props.data.name].length === 0
      : true;

  return (
    <Col grow="1">
      {isMobile && props.title && (
        <MobileTitleAndDescription
          title={props.title}
          description={props.description}
        />
      )}
      <Col
        paddingHorizontal={isMobile ? '36' : undefined}
        paddingBottom={isMobile ? '36' : undefined}
        grow="1"
      >
        {format &&
          format === ScopeDataFormat.Account &&
          accounts.length > 0 && (
            <>
              {!isMobile && (
                <AccountsTable
                  onChange={onAccountChange}
                  onSelectAll={onSelectAll}
                  availableAccounts={accounts}
                  selectedAccountIds={
                    consentClient.values[props.data.name] ?? []
                  }
                  choiceType={props.data.choiceType}
                  title={props.title}
                  description={props.description}
                />
              )}
              {isMobile && (
                <AccountSelection
                  onChange={(d) => {
                    onAccountChange(d).finally(() => {
                      if (props.data.choiceType === ChoiceType.Single) {
                        props.onNext();
                      }
                    });
                  }}
                  availableAccounts={accounts}
                  selectedAccountIds={
                    consentClient.values[props.data.name] ?? []
                  }
                  choiceType={props.data.choiceType}
                />
              )}
            </>
          )}
      </Col>
      {isMobile ? (
        <BottomButtons direction="col" position="fixed" paddingTop="32">
          <Button
            variant="secondary"
            onClick={() => {
              props.onPrevious();
            }}
          >
            {props.previousTitle}
          </Button>
          {!hideNext && (
            <Button
              variant="submit"
              disabled={isDisabled}
              onClick={() => {
                props.onNext();
              }}
            >
              {props.nextTitle}
            </Button>
          )}
        </BottomButtons>
      ) : (
        <FooterWithButtons
          previousTitle={props.previousTitle}
          nextTitle={props.nextTitle}
          isConfirm={props.isLastView}
          disabled={isDisabled}
          hideNext={false}
          onNext={() => {
            props.onNext();
          }}
          onPrevious={() => {
            props.onPrevious();
          }}
        />
      )}
    </Col>
  );
}
